import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Colors } from 'context';
import { careerLayout, careerType } from 'services/careerType';
import { desktop, mobile } from 'services/ui';
import { useMediaQuery } from 'services/hooks/useMediaQuery';
import { useCareerSkillGraph } from 'core/hooks/CareerSkillGraph';
import { useCareerSectionAccess } from 'core/hooks/CareerJourney/useCareerCapabilitySkip';
import { useMyCareerCenter } from 'core/hooks/CareerCenter/useMyCareerCenter';
import { useProfessions } from 'core/hooks/Growth/useProfessions';
import { GrowthSkillMap } from 'components/pages/Growth/parts/GrowthSkillMap';
import { useChooseGoalDialog } from 'organisms/CareerChooseGoalDialog';

import { useSkillMapParams } from './hooks/useSkillMapParams';
import { layoutSkillGraph } from './model/skillGraphLayout';
import { indexSkillGraph, neighbourhoodOf } from './model/skillGraphModel';
import { matchSkills } from './model/skillGraphSearch';
import { coursesOf, filterSkillGraph, hasActiveFilters } from './model/skillGraphFilter';
import { buildEdgePaths } from './model/skillGraphEdges';
import { levelPhrase } from './model/skillLevelLabel';
import { expandedClusters, recommendedNode, revealAnchors } from './model/skillMapExpansion';
import { clampPan, fitToScreen, IViewport, revealBoxes, zoomAt } from './model/viewportMath';
import { CareerSkillPassport } from './parts/CareerSkillPassport';
import { SkillGraphCanvas } from './parts/SkillGraphCanvas';
import { SkillGraphTree } from './parts/SkillGraphTree';
import { SkillMapAnnouncer } from './parts/SkillMapAnnouncer';
import { SkillMapBreadcrumbs } from './parts/SkillMapBreadcrumbs';
import { SkillMapGoalBanner } from './parts/SkillMapGoalBanner';
import { SkillMapLegend } from './parts/SkillMapLegend';
import { SkillMapToolbar } from './parts/SkillMapToolbar';
import { SkillMapZoomControls } from './parts/SkillMapZoomControls';

/**
 * Полноэкранная карта навыков профессии (ТЗ 0070).
 *
 * Экран — рамка вокруг чистых функций: ни одной строки геометрии здесь нет.
 * Всё, что можно проверить тестом, живёт в `model/`, и это не стиль, а вывод
 * из прошлой спеки: правило внутри компонента проверить нечем.
 *
 * 🔴 `graph === null` → отдаём СЕГОДНЯШНЮЮ карточную карту без единой правки.
 * Одна ветка на весь экран закрывает четыре случая разом: у профессии нет
 * структуры, у студента нет цели, бэкенд старше фронта, сеть отвалилась. Пустой
 * холст без объяснения читается как поломка, а не как «данных пока нет».
 */

/**
 * Нижняя граница рабочей области: шесть рядов карточек при масштабе 100 %
 * (6 · 76 = 456). Ниже карта перестаёт быть картой.
 *
 * 🔴 Верхней границы НЕТ. Она была (900) и объяснялась тем, что «верх и низ
 * попадают в поле зрения одновременно» — но это умерло вместе с вписыванием по
 * высоте: лента не помещается в экран ни при каком масштабе. Потолок теперь
 * только отнимал бы у карты высоту на больших мониторах, ровно вопреки задаче
 * «занимать экран».
 */
const MIN_STAGE_HEIGHT = 480;

/**
 * Запасной подвал, пока не измерена настоящая высота легенды: она стоит под
 * картой и без учёта уезжает под сгиб (её собственные 2–3 строки плюс
 * `padding-bottom: 80` у рамки раздела).
 */
const BOTTOM_GUTTER_FALLBACK = 96;

/**
 * Потолок ширины холста.
 *
 * Сцена при нынешних размерах карточек — 872 px в глубину 2 и 1200 с
 * зависимостями; «вписать» больше не увеличивает, поэтому шире 1264 (сцена
 * плюс поля) холсту нечем распорядиться. 1440 — та же максимальная ширина, что
 * у остальных экранов раздела: карта перестаёт быть исключением.
 */
const MAP_MAX_WIDTH = 1440;

export const CareerSkillMap = () => {
  const { t } = useTranslation();
  // Адрес читается ПЕРВЫМ: из него берётся профессия, чью карту запрашиваем.
  const { params, update, trail, goBack } = useSkillMapParams();
  const { graph, loading } = useCareerSkillGraph(params.track);

  /**
   * Право на ленту вакансий называет СЕРВЕР: `myCareerJourney.locked` уже
   * смонтирован в `CareerLayout` над этим экраном, поэтому проверка не стоит
   * ни одного запроса. Паспорт из него делает ровно два вывода — показывать
   * ли переход в ленту и объяснять ли, почему его нет.
   *
   * ⚠️ `pending` (право ещё не приехало) — это НЕ «закрыто»: показать
   * участнику на долю секунды «раздел закрыт» хуже, чем не показать ничего.
   * Поэтому кнопки нет, и объяснения тоже нет.
   */
  const vacancies = useCareerSectionAccess('vacancies');
  /**
   * Список профессий для переключателя, своя цель и мутация выбора.
   *
   * 🔴 Из `careerProfessions`, а НЕ из `myGrowth`. Второй тянет заодно очки,
   * уровни, медали и каталог достижений — платить этим за выпадающий список
   * из шести строк незачем. Тот же хук отдаёт `chooseTrack`, поэтому «сделать
   * целью» с карты и с каталога профессий — одно и то же действие с одним и
   * тем же обновлением кеша.
   */
  const { professions } = useProfessions();
  /**
   * ТЗ 0071, п. 6.6: «Wybierz jako cel» — через диалог подтверждения, а не
   * прямой мутацией. Смена цели перестраивает план, курсы и подбор вакансий;
   * одним случайным кликом по карте такое не делают.
   */
  const { ask: askGoal, dialog: goalDialog } = useChooseGoalDialog();
  const { isPreview } = useMyCareerCenter();
  const tracks = useMemo(
    () => professions.map((row) => ({ code: row.code, title: row.title })),
    [professions],
  );
  const myGoal = useMemo(
    () => professions.find((row) => row.isChosen)?.code || null,
    [professions],
  );

  const isMobile = useMediaQuery(mobile.replace('@media ', ''));
  const isDesktop = useMediaQuery(desktop.replace('@media ', ''));

  const stageRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<IViewport>({ zoom: 1, x: 0, y: 0 });
  const [focused, setFocused] = useState<string | null>(null);
  const [stageHeight, setStageHeight] = useState(MIN_STAGE_HEIGHT);
  /**
   * Ширина вьюпорта БЕЗ вертикального скроллбара — из неё считается вылет
   * карты за колонку оболочки (см. `Root`). Начальное значение берётся лениво,
   * то есть синхронно до первой отрисовки: иначе карта мигнёт узкой.
   */
  const [viewWidth, setViewWidth] = useState(() =>
    typeof document === 'undefined' ? 0 : document.documentElement.clientWidth,
  );
  const lastSize = useRef('');

  const fullIndex = useMemo(() => indexSkillGraph(graph), [graph]);
  const hasGraph = graph !== null;

  const selectedNode = params.skill ? fullIndex.bySlug.get(params.skill) || null : null;

  /**
   * 0073: выбранный, но не сохранённый уровень в паспорте.
   *
   * Живёт здесь, а не в паспорте: закрыть паспорт или сменить навык можно
   * восемью путями (крестик, Escape, клик по холсту, «Cała mapa», крошки,
   * смена профессии, цепочка, «назад»), и все они проходят через эту карту.
   * Каждый спрашивает подтверждение, если оценка не записана. Браузерную
   * «назад» перехватить нельзя (BrowserRouter без блокировщика) — на уход со
   * страницы стоит `beforeunload`.
   */
  const [draft, setDraft] = useState<{ nodeId: string; level: number } | null>(null);
  const draftLevel = draft && selectedNode && draft.nodeId === selectedNode.id ? draft.level : null;
  const passportDirty = draftLevel !== null;
  const dirtyRef = useRef(false);
  dirtyRef.current = passportDirty;

  const onDraftChange = useCallback(
    (level: number | null) => {
      if (!selectedNode) return;
      setDraft(level === null ? null : { nodeId: selectedNode.id, level });
    },
    [selectedNode],
  );

  /** `true` — можно уходить: оценки нет, или человек согласился её потерять. */
  const confirmLeavePassport = useCallback(() => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert -- тот же приём, что у редактора CV
    if (!window.confirm(t('careerCenter.map.assess.leaveConfirm'))) return false;
    dirtyRef.current = false;
    setDraft(null);
    return true;
  }, [t]);

  useEffect(() => {
    if (!passportDirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [passportDirty]);

  /**
   * Навык, запрошенный в адресе, но отсутствующий в показанной профессии, —
   * и профессии, где он есть.
   *
   * 🔴 Считается из уже загруженного списка профессий, а не отдельным
   * запросом: `careerProfessions` несёт навыки каждой профессии, и они на
   * этом экране уже в руках. Оттуда же берётся имя — в графе навыка нет по
   * определению.
   *
   * ⚠️ Навык, не входящий НИ В ОДНУ профессию, остаётся под своим слагом:
   * взять имя больше неоткуда, а ради краевого случая слать второй запрос
   * дороже, чем показать «alembic» вместо «Alembic». Плашка при этом меняет
   * формулировку — без хвоста «встречается здесь», которому нечего перечислить.
   */
  const missingSkill = useMemo(() => {
    if (!params.skill || selectedNode) return { name: '', tracks: [] };
    const slug = params.skill;
    let name = slug;
    // Не `tracks`: так зовётся список профессий выше по файлу, и затенение
    // ловит только eslint — `tsc` на него молчит.
    const where: { code: string; title: string }[] = [];
    professions.forEach((profession) => {
      const hit = profession.skills.find((skill) => skill.slug === slug);
      if (!hit) return;
      name = hit.name;
      where.push({ code: profession.code, title: profession.title });
    });
    return { name, tracks: where };
  }, [params.skill, selectedNode, professions]);

  /**
   * 🔴 Отбор применяется ЗДЕСЬ, выше и раскладки, и списка. Оба вида карты
   * получают один и тот же суженный индекс, и «на холсте три навыка, а в
   * списке двенадцать» становится невозможным по построению, а не по
   * дисциплине правок.
   */
  const filters = useMemo(
    () => ({
      have: params.have,
      core: params.core,
      course: params.course,
    }),
    [params.have, params.core, params.course],
  );

  const filtered = useMemo(
    () => filterSkillGraph(fullIndex, filters, selectedNode?.id || null),
    [fullIndex, filters, selectedNode?.id],
  );
  const { index } = filtered;
  const filtersActive = hasActiveFilters(filters) || Boolean(params.q);
  /** Ни один узел не прошёл отбор — экран обязан сказать это словами. */
  const noMatches = hasActiveFilters(filters) && filtered.shown === 0;

  /** Курсы и группы для панели берутся из ПОЛНОЙ карты, а не из остатка. */
  const courses = useMemo(() => coursesOf(fullIndex), [fullIndex]);

  /**
   * Фокус из адреса, проверенный на существование. Код группы из чужой
   * профессии (старая закладка, смена профессии в адресе руками) ведёт себя как
   * «фокуса нет», а не сворачивает всю карту в ноль раскрытых групп.
   */
  const focus =
    params.focus && fullIndex.clusters.some((row) => row.code === params.focus)
      ? params.focus
      : null;

  /** Рекомендуемый узел — «Mój następny krok» и стартовое раскрытие (ТЗ 0071). */
  const recommended = useMemo(() => recommendedNode(fullIndex), [fullIndex]);

  /**
   * Раскрытые группы (ТЗ 0071, п. 6.1). Правило — чистая функция в
   * `model/skillMapExpansion`; здесь только сбор входа.
   *
   * 🔴 Строкой, а не массивом: массив новый на каждой отрисовке, и раскладка
   * пересчитывалась бы на каждый клик по узлу.
   */
  const showAll = hasActiveFilters(filters) || params.q.length >= 2;
  const expansionBase = useMemo(
    () => ({
      depth: params.depth,
      focus,
      recommendedCluster: recommended?.clusterCode || null,
      firstCluster: index.clusters[0]?.code || null,
      showAll,
    }),
    [params.depth, focus, recommended, index.clusters, showAll],
  );
  const expandedKey = useMemo(() => {
    const open = expandedClusters({
      ...expansionBase,
      selectedCluster: selectedNode?.clusterCode || null,
    });
    return open === null ? null : open.join('|');
  }, [expansionBase, selectedNode?.clusterCode]);
  const expanded = useMemo(
    () => (expandedKey === null ? null : expandedKey.split('|').filter(Boolean)),
    [expandedKey],
  );

  const layout = useMemo(
    () =>
      layoutSkillGraph(index, {
        depth: params.depth,
        deps: params.deps,
        selected: selectedNode?.id || null,
        expanded,
      }),
    // 🔴 Выбор узла в ключ НЕ входит: пересчитывать раскладку на каждый клик
    // значит перерисовывать всю карту ради подсветки. Единственный случай,
    // когда выбор на неё влияет — втягивание спрятанного узла, — и он учтён
    // через `selectedNode`.
    [index, params.depth, params.deps, selectedNode?.id, expanded],
  );

  const found = useMemo(
    () => (params.q.length >= 2 ? matchSkills(index, params.q) : null),
    [index, params.q],
  );

  const highlighted = useMemo(() => {
    if (found && found.length) return new Set(found.map((row) => row.id));
    return new Set<string>();
  }, [found]);

  const edges = useMemo(
    () => buildEdgePaths(index, layout, { deps: params.deps, selected: selectedNode?.id || null }),
    [index, layout, params.deps, selectedNode?.id],
  );

  /**
   * Высота карты МЕРЯЕТСЯ от её собственного верха, а не задаётся константой.
   *
   * ⚠️ ТЗ называет `calc(100vh - 220px)`, и это число не сходится с оболочкой
   * LMS: шапка сайта, вкладки раздела, панель и крошки съедают около 330 px,
   * так что при 220 нижний край карты уезжает под сгиб — ровно то, чего ТЗ и
   * добивалось запретом «уменьшать карту до карточки». Меряем реальный отступ
   * сверху: на любом экране карта занимает всё, что осталось.
   *
   * 🔴 Подвал тоже МЕРЯЕТСЯ, а не берётся константой 32. Под картой стоит
   * легенда — единственное место, где расшифрован штрих рамки; при постоянном
   * подвале она уезжала под сгиб на каждом экране, где карта заняла всю
   * высоту. Считаем её настоящую высоту: карта заканчивается ровно там, где
   * начинается легенда, и страница перестаёт прокручиваться вовсе — а это
   * важнее, чем кажется, потому что колесо над картой перехвачено панорамой, и
   * «карта едет, а страница стоит» выглядит поломкой ровно до тех пор, пока
   * странице есть куда ехать.
   *
   * 🔴 Ширина вьюпорта берётся из `documentElement.clientWidth`, а НЕ из
   * `100vw`: `vw` включает ширину классического скроллбара, и карта во всю
   * ширину окна давала бы всей странице горизонтальную прокрутку.
   */
  const measure = useCallback(() => {
    const top = stageRef.current?.getBoundingClientRect?.().top ?? 0;
    const legend = legendRef.current?.getBoundingClientRect?.().height ?? 0;
    const gutter = legend ? legend + 24 : BOTTOM_GUTTER_FALLBACK;
    const available = window.innerHeight - top - gutter;
    setStageHeight(Math.max(MIN_STAGE_HEIGHT, Math.round(available)));
    setViewWidth(document.documentElement.clientWidth);
  }, []);

  /**
   * Якоря исходного вида: корень профессии, раскрытая группа и целевой узел —
   * выбранный, иначе рекомендуемый.
   *
   * 🔴 Живут в РЕФЕ, а не в зависимостях `fit`. Иначе каждый клик по узлу
   * менял бы якоря, пере-вписывал карту, и холст прыгал бы под курсором.
   * Вписывание срабатывает на смене размеров сцены, окна и раскрытой группы, а
   * якоря берёт самые свежие.
   */
  const anchorsRef = useRef<string[]>([]);
  anchorsRef.current = revealAnchors(layout, expanded, selectedNode?.id || recommended?.id || null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const fit = useCallback(() => {
    const box = stageRef.current?.getBoundingClientRect?.();
    const view = { width: box?.width || 960, height: box?.height || 640 };
    const { current } = layoutRef;
    const scene = { width: current.width, height: current.height };
    const boxes = anchorsRef.current
      .map((key) => current.byKey.get(key))
      .filter((card): card is NonNullable<typeof card> => Boolean(card));
    // 🔴 ТЗ 0071, п. 6.1: вписывание прижимало ленту к левому верхнему углу, и
    // раскрытая группа внизу ленты оставалась за краем — вместе с узлом, ради
    // которого карту открыли. Теперь исходный вид подводит якоря в кадр.
    setViewport(revealBoxes(fitToScreen(scene, view), boxes, scene, view));
  }, []);

  // Первая отрисовка и смена структуры вписывают карту сами: человек не должен
  // начинать знакомство с экраном с прокрутки.
  // 🔴 `graph` в зависимостях обязателен. На первом кадре экран показывает
  // «загружаем», карты в разметке ещё нет, и `stageRef.current` пуст — замер
  // считает отступ сверху нулевым и оставляет высоту максимальной. Дальше
  // эффект без этой зависимости не повторяется никогда, и нижний край карты
  // навсегда уезжает под сгиб. Поймано глазами: гейты видят зелёный экран.
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, graph]);

  useEffect(() => {
    fit();
    // Раскрытие другой группы — смена структуры, даже если размер сцены
    // случайно совпал: без `expandedKey` карта осталась бы смотреть на
    // прежнюю группу.
  }, [fit, stageHeight, layout.width, layout.height, expandedKey]);

  /**
   * 🔴 Пере-вписывание при смене РАЗМЕРА сцены, а не только при смене данных.
   *
   * ТЗ 0071: паспорт на desktop снова отнимает у холста ширину — он стоит
   * колонкой справа, — и наблюдатель пере-вписывает карту с якорем на
   * выбранном узле. Кроме того сцена меняет размер сама: сворачивается шапка
   * LMS, появляется
   * плашка урезанной карты, переключается вид «холст ↔ список», меняется зум
   * окна. Без наблюдателя карта в этих случаях оставалась в прежних
   * координатах, и колонка навыков уезжала за край. Поймано глазами; ни один
   * гейт такого не видит.
   *
   * 🔴 Наблюдатель ТОЛЬКО вписывает. Пересчитывать отсюда ширину вьюпорта
   * (`viewWidth`) нельзя: ширина холста сама выведена из неё, и вышла бы
   * петля «стало шире → появился вертикальный скроллбар → стало уже → полоса
   * пропала → стало шире». Ширину и высоту меряет `measure` по `resize` окна.
   */
  useEffect(() => {
    const node = stageRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box) return;
      const size = `${Math.round(box.width)}x${Math.round(box.height)}`;
      // ⚠️ Наблюдатель срабатывает СРАЗУ при подписке, а не только на
      // изменение. Без этой проверки он перебивал подведение выбранного узла в
      // поле зрения: два эффекта гонялись, и побеждал тот, что просто вписывал
      // карту заново.
      if (lastSize.current === size) return;
      lastSize.current = size;
      fit();
    });
    observer.observe(node);
    return () => observer.disconnect();
    // 🔴 `hasGraph` обязателен: `fit` теперь стабилен (якоря и раскладка — в
    // рефах), а на первом кадре экран показывает «загружаем», и холста в
    // разметке нет. Без этой зависимости наблюдатель не подписался бы никогда.
  }, [fit, hasGraph]);

  const stageBox = useCallback(() => {
    const box = stageRef.current?.getBoundingClientRect?.();
    return { width: box?.width || 960, height: box?.height || 640 };
  }, []);

  /**
   * 🔴 Панорама ОБЯЗАТЕЛЬНА, а не украшение. Масштаб не опускается ниже 0.8 —
   * ниже подписи не читаются, — и карта из двенадцати рядов в это окно просто
   * не помещается. Без перетаскивания нижние узлы недостижимы вовсе: экран
   * выглядит рабочим и молча прячет треть профессии.
   */
  const onPan = useCallback(
    (dx: number, dy: number) => {
      setViewport((current) =>
        clampPan(
          { ...current, x: current.x + dx, y: current.y + dy },
          { width: layout.width, height: layout.height },
          stageBox(),
        ),
      );
    },
    [layout.width, layout.height, stageBox],
  );

  /**
   * Кнопки масштаба оверлея. Масштаб — вокруг ЦЕНТРА холста, а не левого
   * верхнего угла: иначе «+» уводил бы из кадра то, на что смотрели.
   */
  const onZoomBy = useCallback(
    (factor: number) => {
      const box = stageBox();
      setViewport((current) =>
        clampPan(
          zoomAt(current, factor, { x: box.width / 2, y: box.height / 2 }),
          { width: layout.width, height: layout.height },
          box,
        ),
      );
    },
    [layout.width, layout.height, stageBox],
  );

  const onWheelZoom = useCallback(
    (factor: number, point: { x: number; y: number }) => {
      setViewport((current) =>
        clampPan(
          zoomAt(current, factor, point),
          { width: layout.width, height: layout.height },
          stageBox(),
        ),
      );
    },
    [layout.width, layout.height, stageBox],
  );

  /**
   * Подвести узел под фокусом или выбором в поле зрения.
   *
   * 🔴 Приёмочный критерий ТЗ: «правый паспорт не перекрывает выбранный узел».
   * Узел выбирают не только кликом по холсту: ещё чипом внутри паспорта,
   * поиском и клавиатурой. Во всех трёх случаях выбранный узел обязан
   * оказаться в поле зрения рядом с открытым паспортом.
   * Двигаем ТОЛЬКО если узел действительно вне видимой части: иначе карта
   * дёргалась бы на каждом клике.
   *
   * 🔴 `focused` — ПЕРВЫМ, и это не мелочь. Стрелки двигают именно фокус, а не
   * выбор; лента длиннее экрана в несколько раз, поэтому на третьем нажатии
   * кольцо фокуса уезжало за кадр, и дальше экран выглядел замершим — при том
   * что скринридер честно вёл человека по узлам, которых на экране нет.
   */
  useEffect(() => {
    const targetId = focused || selectedNode?.id || null;
    const card = targetId ? layout.byKey.get(targetId) : null;
    if (!card) return;
    const box = stageRef.current?.getBoundingClientRect?.();
    if (!box) return;
    setViewport((current) => {
      const left = card.x * current.zoom + current.x;
      const top = card.y * current.zoom + current.y;
      const right = left + card.w * current.zoom;
      const bottom = top + card.h * current.zoom;
      if (left >= 0 && top >= 0 && right <= box.width && bottom <= box.height) {
        return current;
      }
      return clampPan(
        {
          zoom: current.zoom,
          x: box.width / 2 - (card.x + card.w / 2) * current.zoom,
          y: box.height / 2 - (card.y + card.h / 2) * current.zoom,
        },
        { width: layout.width, height: layout.height },
        { width: box.width, height: box.height },
      );
    });
    // ⚠️ `stageHeight` в зависимостях обязателен. Замер высоты меняет её уже
    // ПОСЛЕ первого кадра с данными, и следом срабатывает вписывание — оно
    // перетирало подведение узла, потому что этот эффект к тому моменту уже
    // отработал и больше не повторялся.
  }, [focused, selectedNode, layout, stageHeight]);

  /**
   * Закрыть паспорт.
   *
   * 🔴 Если группа закрываемого навыка раскрыта ТОЛЬКО из-за выбора (навык
   * открыли чипом из соседней группы), закрытие раскрывает её явно. Иначе
   * вместе с паспортом сворачивалась бы и группа, и фокус, который паспорт
   * возвращает на узел, возвращать было бы некуда.
   */
  const closePassport = useCallback(() => {
    if (!selectedNode) return false;
    if (!confirmLeavePassport()) return false;
    const code = selectedNode.clusterCode;
    const after = expandedClusters({ ...expansionBase, selectedCluster: null });
    const keepOpen = after !== null && !after.includes(code) && code !== '__orphans__';
    update({ skill: null, ...(keepOpen ? { focus: code, depth: 2 as const } : {}) }, []);
    // Кольцо фокуса остаётся на узле, с которого пришли.
    setFocused(selectedNode.id);
    return true;
  }, [selectedNode, expansionBase, update, confirmLeavePassport]);

  const onSelect = useCallback(
    (nodeId: string) => {
      const node = nodeId ? index.byId.get(nodeId) : null;
      if (!node) {
        closePassport();
        return;
      }
      if (node.id !== selectedNode?.id && !confirmLeavePassport()) return;
      // Клик по карте — начало НОВОЙ цепочки: возвращаться в прежнюю ветку
      // после того, как человек ушёл на другой конец карты, некуда.
      update({ skill: node.slug }, []);
      setFocused(nodeId);
    },
    [index, update, closePassport, selectedNode, confirmLeavePassport],
  );

  /**
   * «Mój następny krok» (ТЗ 0071, п. 6.2): выделить рекомендуемый узел,
   * раскрыть его группу и открыть паспорт. Одним действием — это не три
   * шага, а один ответ на вопрос «что мне делать».
   */
  const onNextStep = useCallback(() => {
    if (!recommended) return;
    if (recommended.id !== selectedNode?.id && !confirmLeavePassport()) return;
    update({ skill: recommended.slug, focus: recommended.clusterCode, depth: 2 }, []);
    setFocused(recommended.id);
  }, [recommended, update, selectedNode, confirmLeavePassport]);

  /** Раскрыть группу из списка — тот же жест, что клик по группе на холсте. */
  const onExpandGroup = useCallback((code: string) => update({ focus: code, depth: 2 }), [update]);

  /**
   * «Cała mapa» (ТЗ 0071, п. 6.2): обзор профессии и всех групп — глубина 1
   * без фокуса. Паспорт закрывается: обзор всей карты и паспорт одного навыка
   * — два разных вопроса.
   */
  const onWholeMap = useCallback(() => {
    if (!confirmLeavePassport()) return;
    update({ depth: 1, focus: null, skill: null }, []);
    setFocused(null);
  }, [update, confirmLeavePassport]);

  /**
   * Переход по цепочке внутри паспорта: чипы «сначала нужно» и «открывает».
   *
   * 🔴 Прежний навык кладётся в стек, иначе через два шага вглубь вернуться
   * можно только браузерной «назад» — а в паспорте на неё не рассчитывают и
   * закрывают его крестиком, теряя место, куда смотрели.
   */
  const onOpenSkill = useCallback(
    (slug: string) => {
      if (slug !== params.skill && !confirmLeavePassport()) return;
      update({ skill: slug }, params.skill ? [...trail, params.skill] : trail);
    },
    [params.skill, trail, update, confirmLeavePassport],
  );

  const onZoomCards = useCallback(
    (key: string) => {
      if (key.startsWith('more:')) {
        // Клик по заглушке переносит фокус на КОЛОНКУ и раскрывает её целиком:
        // раскрытая явно группа показывается без обрезки.
        update({ focus: key.slice('more:'.length), depth: 2 });
        return;
      }
      if (key.startsWith('cluster:')) {
        // ТЗ 0071: раскрытие группы — глубина 2, даже из «Całej mapy»; соседние
        // группы остаются на карте свёрнутыми.
        update({ focus: key.slice('cluster:'.length), depth: 2 });
        return;
      }
      if (key === 'track') update({ focus: null });
    },
    [update],
  );

  /**
   * Сброс отбора. Паспорт (`skill`), режим показа и профессия НЕ трогаются:
   * кнопка называется «сбросить фильтры», и закрывать ею открытую карточку
   * навыка или уводить с чужой карты было бы неожиданно.
   */
  const onResetFilters = useCallback(() => {
    // Фокус больше не фильтр (ТЗ 0071): сброс не сворачивает раскрытую группу.
    update({ q: '', have: null, core: false, course: null });
  }, [update]);

  /**
   * Смена профессии.
   *
   * 🔴 Фокус, курс и выбранный навык обнуляются принудительно: их значения —
   * коды из ПРЕЖНЕЙ карты, и в новой не найдутся. Фокус на чужой код давал бы
   * пустой экран. А вот отбор по статусу, «ключевые» и поиск сохраняются: «чего
   * мне не хватает вон в той профессии» — это и есть смысл переключателя.
   */
  const onChangeTrack = useCallback(
    (code: string | null) => {
      if (!confirmLeavePassport()) return;
      update({ track: code, focus: null, course: null, skill: null });
    },
    [update, confirmLeavePassport],
  );

  /**
   * Сделать просматриваемую профессию своей целью — через подтверждение.
   * После сохранения диалог сам уводит на `/career`: там видны новая цель и
   * следующее действие.
   */
  const onChooseGoal = useCallback(() => {
    if (!graph) return;
    askGoal({ code: graph.trackCode, title: graph.trackTitle });
  }, [graph, askGoal]);

  /**
   * Переход по крошке.
   *
   * 🔴 `skill: null` обязателен. Прежде крошки звали тот же обработчик, что и
   * клик по карточке группы, а он меняет только фокус — и открытый паспорт
   * оставался висеть поверх перестроенной карты.
   */
  const onGoToCrumb = useCallback(
    (key: string) => {
      if (!confirmLeavePassport()) return;
      if (key === 'track') {
        update({ focus: null, skill: null });
        return;
      }
      if (key.startsWith('cluster:')) {
        update({ focus: key.slice('cluster:'.length), depth: 2, skill: null });
      }
    },
    [update, confirmLeavePassport],
  );

  /** Куда ведёт кнопка «назад» в паспорте — со своим именем, а не стрелкой. */
  const backTo = useMemo(() => {
    const previous = trail[trail.length - 1];
    if (!previous) return null;
    const node = fullIndex.bySlug.get(previous);
    return node ? { slug: node.slug, name: node.name } : null;
  }, [trail, fullIndex]);

  const announcement = useMemo(() => {
    if (noMatches) return t('careerCenter.map.emptyFilters');
    if (filtersActive) {
      return t('careerCenter.map.shownOf', { shown: filtered.shown, total: filtered.total });
    }
    if (found) return t('careerCenter.map.found', { n: found.length });
    if (selectedNode) {
      return t('careerCenter.map.nodeLabel', {
        name: selectedNode.name,
        status: t(`careerCenter.map.status.${selectedNode.status}`),
        levels: t(levelPhrase(selectedNode).key, levelPhrase(selectedNode).vars),
      });
    }
    return t('careerCenter.map.shown', { n: layout.cards.length });
  }, [
    noMatches,
    filtersActive,
    filtered.shown,
    filtered.total,
    found,
    selectedNode,
    layout.cards.length,
    t,
  ]);

  if (loading) return <Note>{t('careerCenter.map.loading')}</Note>;

  // Одна ветка вместо тернарников по двадцати компонентам.
  //
  // 🔴 Но только для СВОЕЙ карты. `GrowthSkillMap` — это карта МОИХ навыков; на
  // запрос «покажи профессию X» она ответила бы про другое, и человек решил бы,
  // что смотрит X.
  if (graph === null) {
    if (!params.track) return <GrowthSkillMap />;
    return (
      <ViewingOther>
        <span>{t('careerCenter.map.noMapForTrack')}</span>
        <NoteButton type="button" onClick={() => onChangeTrack(null)}>
          {t('careerCenter.map.backToGoal')}
        </NoteButton>
      </ViewingOther>
    );
  }

  const useTree = params.view === 'tree' || (params.view === null && isMobile);

  // Прижимать ли карту к краям окна. В режиме списка — нет: строка длиной во
  // весь монитор нечитаема, а на телефоне список и так стоит по умолчанию.
  // Доработка 0071 (15.09): слева от карты теперь боковое меню раздела.
  // Выход за колонку отрицательными отступами симметричен и наехал бы на меню,
  // поэтому карта живёт внутри своей колонки; ширину ей даёт свёрнутый рельс.
  const bleed = false;
  const moreAbove = !useTree && viewport.y < -1;
  const moreBelow = !useTree && layout.height * viewport.zoom + viewport.y > stageHeight + 1;

  return (
    <Root $bleed={bleed} style={{ '--skm-vw': `${viewWidth}px` } as React.CSSProperties}>
      <SkillMapAnnouncer message={announcement} />
      <Help id="skm-help">{t('careerCenter.map.help')}</Help>

      <Header>
        <SkillMapBreadcrumbs
          index={fullIndex}
          selected={selectedNode?.id || null}
          focus={focus}
          tracks={tracks}
          currentTrack={graph.trackCode}
          onGoTo={onGoToCrumb}
          onChangeTrack={onChangeTrack}
        />
        <SkillMapToolbar
          params={params}
          found={found ? found.length : null}
          clusters={fullIndex.clusters}
          courses={courses}
          shown={filtered.shown}
          total={filtered.total}
          filtersActive={filtersActive}
          hasNextStep={Boolean(recommended)}
          onChange={update}
          onReset={onResetFilters}
          onNextStep={onNextStep}
          onWholeMap={onWholeMap}
        />
      </Header>

      {/* ТЗ 0071, пп. 6.1 и 6.6: цель не выбрана или смотрится чужая профессия.
          Просмотр цель НЕ меняет — только кнопка с подтверждением. Выход «к
          своей карте» — лишь когда своя цель известна и она другая. */}
      <SkillMapGoalBanner
        graph={graph}
        canChoose={!isPreview}
        onChoose={onChooseGoal}
        onBackToGoal={
          params.track && myGoal && graph.trackCode !== myGoal
            ? () => onChangeTrack(null)
            : undefined
        }
      />

      {/* 🔴 Запрошен навык, которого в ЭТОЙ профессии нет. Так приходят по
          ссылке с занятия: «React» на уроке фронтенда открывает карту цели, а
          цель у студента питоновская. Без этой плашки экран молчал вовсе —
          человек нажал на навык и увидел чужую карту без единого слова о том,
          что с его запросом. Профессии, где навык есть, берутся из УЖЕ
          загруженного списка: лишнего запроса не делаем. */}
      {params.skill && !selectedNode && (
        <ViewingOther>
          <NoteText>
            {/* 🔴 Две формулировки, а не одна. «Встречается здесь:» с пустым
                хвостом — обещание списка, которого не будет: так выглядит и
                навык, не входящий ни в одну профессию, и та секунда, пока
                справочник профессий ещё летит. */}
            {t(
              missingSkill.tracks.length
                ? 'careerCenter.map.skillNotHere'
                : 'careerCenter.map.skillNotHereAlone',
              { name: missingSkill.name, title: graph.trackTitle },
            )}
          </NoteText>
          {missingSkill.tracks.map((row) => (
            <NoteButton
              key={row.code}
              type="button"
              // 🔴 Не `onChangeTrack`: тот сбрасывает выбранный навык вместе с
              // фокусом и фильтрами, а здесь навык — единственная причина
              // перехода. Сбросить его значило бы привести человека к той же
              // карте без ответа, только другой.
              onClick={() => update({ track: row.code, focus: null, course: null })}>
              {row.title}
            </NoteButton>
          ))}
          <NoteButton type="button" onClick={() => update({ skill: null })}>
            {t('careerCenter.map.skillNotHereClose')}
          </NoteButton>
        </ViewingOther>
      )}

      {graph.isFallback && <Note>{t(`careerCenter.map.fallback.${graph.fallbackReason}`)}</Note>}

      {noMatches && <Note>{t('careerCenter.map.emptyFilters')}</Note>}

      {/* ТЗ 0071, п. 6.4: на desktop паспорт — колонка 420 px СПРАВА от
          холста, без маски; холст сужается и остаётся кликабельным. Ниже 1220
          паспорт — полноэкранная панель, и колонки нет. */}
      <Workspace $split={isDesktop && Boolean(selectedNode)}>
        <MapColumn>
          <Stage ref={stageRef} $tree={useTree} $height={stageHeight}>
            {useTree ? (
              <SkillGraphTree
                index={index}
                selected={selectedNode?.id || null}
                onSelect={onSelect}
                expanded={expanded}
                onExpand={onExpandGroup}
              />
            ) : (
              <SkillGraphCanvas
                index={index}
                layout={layout}
                viewport={viewport}
                selected={selectedNode?.id || null}
                focused={focused}
                deps={params.deps}
                highlighted={highlighted.size ? highlighted : neighbourhoodOf(index, null)}
                onSelect={onSelect}
                onFocusChange={setFocused}
                onZoomCards={onZoomCards}
                onPan={onPan}
                onWheelZoom={onWheelZoom}
              />
            )}
            {/* Затухающий край — знак «содержимое продолжается». Лента длиннее
                экрана по построению, а скроллбара у холста нет: без этого знака
                нижние группы просто не существуют для того, кто не догадался
                потянуть карту мышью. Клику не мешает и вслух не читается. */}
            {moreAbove && <Fade $side="top" aria-hidden />}
            {moreBelow && <Fade $side="bottom" aria-hidden />}
            {!useTree && (
              <SkillMapZoomControls
                zoom={viewport.zoom}
                onZoomIn={() => onZoomBy(1.2)}
                onZoomOut={() => onZoomBy(1 / 1.2)}
                onReset={fit}
              />
            )}
          </Stage>

          {/* Легенда — справка, а не управление: под картой она не отнимает у
              неё высоту, которой на ноутбуке и так нет. Её высота МЕРЯЕТСЯ (см.
              `measure`) — иначе карта заканчивается под сгибом. */}
          <div ref={legendRef}>
            <SkillMapLegend capped={edges.capped} />
          </div>
        </MapColumn>

        <CareerSkillPassport
          index={index}
          node={selectedNode}
          recommended={graph.recommendedCourses}
          canOpenVacancies={!vacancies.locked && !vacancies.pending}
          vacanciesLocked={vacancies.locked}
          onOpenSkill={onOpenSkill}
          backTo={backTo}
          onBack={() => (confirmLeavePassport() ? goBack() : undefined)}
          onClose={closePassport}
          height={useTree ? undefined : stageHeight}
          trackCode={graph.trackCode}
          draftLevel={draftLevel}
          onDraftChange={onDraftChange}
        />
      </Workspace>
      {goalDialog}
    </Root>
  );
};

/**
 * Карта выходит за колонку оболочки.
 *
 * 🔴 Ширину контента в LMS держит `MainTemplate` (`max-width: 1110`), и трогать
 * его нельзя: он красит КАЖДЫЙ экран. Поэтому карта вылезает наружу сама —
 * отрицательными логическими маргинами. Проценты в маргинах считаются от
 * ширины контейнера, поэтому `50% - половина окна` ровно доводит секцию до
 * краёв вьюпорта, а `min(…, MAP_MAX_WIDTH / 2)` не даёт ей расползтись на
 * большом мониторе.
 *
 * 🔴 `margin: 0 auto` пришлось СНЯТЬ: авто-маргины по поперечной оси отменяют
 * растяжение flex-элемента, и секция брала ширину по содержимому — из-за этого
 * прежний `max-width: 1440` не срабатывал никогда.
 *
 * 🔴 Ширина окна приходит переменной `--skm-vw` из замера
 * `documentElement.clientWidth`, а не из `100vw`: `vw` включает классический
 * скроллбар, и страница получила бы горизонтальную прокрутку. `100vw` остаётся
 * только запасным значением на первый кадр серверной отрисовки.
 */
const Root = styled.section<{ $bleed: boolean }>`
  position: relative;
  padding: 0 ${careerLayout.gutterMobile}px;
  display: flex;
  flex-direction: column;
  /* Секция тоже не даёт себя сжать — иначе холст внутри останется без
     высоты, сколько бы её ни намерили. */
  flex: none;
  gap: 12px;

  ${({ $bleed }) =>
    $bleed
      ? css`
          margin-inline: calc(50% - min(var(--skm-vw, 100vw) / 2, ${MAP_MAX_WIDTH / 2}px));
        `
      : css`
          /* «Визуальное упрощение» (15.09): поля держит оболочка раздела, карта
             занимает всю правую колонку без предела ширины. */
          width: 100%;
          padding: 0;
        `}

  ${desktop} {
    padding: ${({ $bleed }) => ($bleed ? `0 ${careerLayout.gutterDesktop}px` : '0')};
  }
`;

/**
 * Крошки и панель — ОДНОЙ строкой.
 *
 * Колонкой они стояли, пока панель занимала два ряда по 62 px: тогда соседство
 * с крошками стоило лишнего переноса. Панель ужалась до одного ряда в 28 px, и
 * теперь колонка стоит холсту 40 px впустую.
 *
 * Крошки — резинка (`flex: 1 1 0` и `min-width: 0`), панель фиксирована: при
 * нехватке места усекается название профессии, а не пропадают органы
 * управления. Ниже 1220 обе снова встают колонкой.
 */
const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${desktop} {
    flex-direction: row;
    align-items: center;
    gap: 12px;

    & > nav {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

/**
 * Холст и паспорт рядом (ТЗ 0071, п. 6.4).
 *
 * 🔴 Колонки, а не наложение: панель поверх правого края прятала бы ровно ту
 * колонку навыков, где стоит выбранный узел. Холст сужается, наблюдатель
 * размера пере-вписывает карту, а якоря вписывания держат выбранный узел в
 * кадре. `minmax(0, 1fr)` — иначе широкий svg распирает колонку.
 */
const Workspace = styled.div<{ $split: boolean }>`
  display: ${({ $split }) => ($split ? 'grid' : 'flex')};
  flex-direction: column;
  grid-template-columns: minmax(0, 1fr) 420px;
  align-items: start;
  gap: 16px;
`;

const MapColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

/**
 * Высота карты.
 *
 * ⚠️ Числа не из ТЗ: там стоит `calc(100vh - 220px)` с нижней границей 680, и
 * оно не сходится с оболочкой LMS — шапка, вкладки раздела, панель и крошки
 * съедают около 330 px, так что при 680 нижний край карты уезжает под сгиб на
 * ноутбучном экране. Поэтому высота МЕРЯЕТСЯ вместе с подвалом (см. `measure`),
 * а 480 — предел, ниже которого карта перестаёт быть картой. Верхнего предела
 * нет: см. комментарий к `MIN_STAGE_HEIGHT`.
 */
const Stage = styled.div<{ $tree: boolean; $height: number }>`
  position: relative;
  /* 🔴 flex: none, а НЕ flex: 1. Рамка раздела вложена в колонку с
     min-height: calc(100vh - 345px), и flex-элементы в ней сжимаются: при
     flex: 1 холст с честно посчитанной высотой 681 px рисовался высотой 150 —
     правило было, а сжатие его молча перебивало. Поймано глазами; ни tsc, ни
     тесты, ни сборка такого не видят.
     (Обратные кавычки в комментариях внутри styled-шаблона ставить нельзя:
     они обрывают сам шаблон.) */
  flex: none;
  min-width: 0;
  border: 1px solid ${Colors.system_gray};
  border-radius: ${careerLayout.cardRadius}px;
  overflow: ${({ $tree }) => ($tree ? 'auto' : 'hidden')};
  background: ${Colors.white};
  height: ${({ $tree, $height }) => ($tree ? 'auto' : `${$height}px`)};
  padding: ${({ $tree }) => ($tree ? '16px' : '0')};
`;

/**
 * Затухающий край сверху и снизу холста.
 *
 * 🔴 Цвет — токен `Colors.white`, а не литеральный `#fff`: токен
 * переворачивается вместе с тёмной темой, и полоса не превращается в белый
 * мазок поверх карты. Гейт контраста литерал внутри градиента не поймал бы.
 */
const Fade = styled.div<{ $side: 'top' | 'bottom' }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 56px;
  pointer-events: none;
  ${({ $side }) => ($side === 'top' ? 'top: 0;' : 'bottom: 0;')}
  background: linear-gradient(
    ${({ $side }) => ($side === 'top' ? 'to bottom' : 'to top')},
    ${Colors.white},
    transparent
  );
`;

/**
 * Строка «вы смотрите чужую карту»: объяснение и два выхода из положения.
 *
 * Была полосой в 60 px с кнопками по 44 — то есть отъедала у холста больше,
 * чем сама панель. Стала строкой статуса в 32: длинное название профессии
 * усекается, кнопки остаются целыми, потому что без них строка бесполезна.
 */
const ViewingOther = styled.div`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${careerType.meta()};
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget + 8}px;
  padding: 4px 4px 4px 12px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  color: ${Colors.black};
  background: ${Colors.bg_gray};
`;

const NoteText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoteButton = styled.button`
  ${careerType.metaStrong()};
  flex: 0 0 auto;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  color: ${Colors.black};
  background: ${Colors.white};
  border: 1px solid ${Colors.black};

  &:disabled {
    color: ${Colors.gray_dark};
    border-color: ${Colors.system_gray};
    cursor: not-allowed;
  }
`;

const Note = styled.p`
  ${careerType.body()};
  color: ${Colors.gray_dark};
  margin: 0;
`;

/** Подсказка по клавишам — скрыта визуально, читается скринридером. */
const Help = styled.p`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
